/**
 * Interface scale — the zoom factor the whole UI is drawn at.
 *
 * The value itself lives in the main process (electron/uiScale.ts): it is
 * applied as the window's own `zoomFactor` before the first frame, so a
 * renderer-owned copy in localStorage would always be the second place to
 * learn about it. This store is the renderer's *view* of that value — seeded
 * from the launch payload, refreshed when the settings panel opens, and pushed
 * back through the bridge when the user picks a different one.
 *
 * Two readers: the settings control, and the title bar — on macOS the traffic
 * lights are drawn outside the page at a fixed size, so the padding that clears
 * them has to be divided by whatever the page is scaled to.
 */

import { create } from 'zustand';
import { isBrowserMode } from '../../browser-mode/browserModeUtils';
import { getWindowChrome } from '../model/windowChrome';

interface UiScaleState {
  scale: number;
  /** What this platform uses when the user has never chosen (Windows draws smaller). */
  platformDefault: number;
  /** The offered steps, as the main process defines them. Empty until loaded. */
  steps: number[];
  /** Pull the scale actually in effect, plus the steps to offer. */
  load: () => Promise<void>;
  /** Persist a scale and apply it to the live window. */
  setScale: (scale: number) => Promise<void>;
}

/**
 * Whether there is a window to scale. Browser mode is checked explicitly
 * rather than by probing the bridge: its shim implements the whole
 * `appSettings` surface (the shared type requires it) and answers with a
 * no-op, so a presence check would offer a control that does nothing.
 */
export function supportsUiScale(): boolean {
  return !isBrowserMode() && typeof window.electronAPI?.appSettings?.setUiScale === 'function';
}

/** The label the control and telemetry both use — `formatUiScale` in electron/uiScale.ts. */
export function formatUiScale(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

export const useUiScaleStore = create<UiScaleState>((set, get) => ({
  // The launch payload carries what main applied, so the first render of the
  // title bar already has the right number to divide its inset by.
  scale: getWindowChrome().uiScale ?? 1,
  platformDefault: getWindowChrome().uiScale ?? 1,
  steps: [],

  load: async () => {
    const bridge = window.electronAPI?.appSettings;
    if (!bridge?.getUiScale) return;

    try {
      const { scale, platformDefault, steps } = await bridge.getUiScale();
      set({ scale, platformDefault, steps });
    } catch {
      /* leave the seeded value in place — the window is already drawn at it */
    }
  },

  setScale: async (scale) => {
    if (scale === get().scale) return;

    const bridge = window.electronAPI?.appSettings;
    if (!bridge?.setUiScale) return;

    // Optimistic: the zoom lands on the next frame either way, and a failed
    // write would leave the control disagreeing with what is on screen.
    set({ scale });

    try {
      const result = await bridge.setUiScale(scale);
      if (result?.scale !== undefined) set({ scale: result.scale });
    } catch {
      /* keep the optimistic value; nothing else can tell us better */
    }
  },
}));
